"use client";

import { useEffect, useState } from "react";
import { Play, Slash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
export default function Otahero() {
  const [currentTime, setCurrentTime] = useState("06:00");
  useEffect(() => {
    const times = ["06:00", "09:00", "12:00", "15:00", "18:00", "21:00"];
    let index = 0;
    const interval = setInterval(() => {
      index = (index + 1) % times.length;
      setCurrentTime(times[index]);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [{
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://hotelrevbaba.com/HomePage"
    }, {
      "@type": "ListItem",
      "position": 2,
      "name": "Services",
      "item": "https://hotelrevbaba.com/ServicesPage"
    }, {
      "@type": "ListItem",
      "position": 3,
      "name": "OTA Listing Optimization",
      "item": "https://hotelrevbaba.com/OTAListingOptimizationServices2Page"
    }]
  };
  return <section data-section-id="2838" id="otahero" className="relative min-h-screen w-full overflow-hidden bg-slate-50 text-slate-900">
      <div className="absolute left-8 top-20 z-30 md:left-12 md:top-24 lg:left-24">
        <Breadcrumb>
          <BreadcrumbList className="font-default text-[10px] uppercase tracking-[0.2em] text-white/60">
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/HomePage" className="hover:text-secondary transition-colors">
                  Home
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator>
              <Slash className="size-3 opacity-30" />
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="#" className="hover:text-secondary transition-colors">
                  Services
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator>
              <Slash className="size-3 opacity-30" />
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage className="text-secondary font-bold">
                OTA Listing Optimization
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(breadcrumbSchema)
    }} />

      <div className="absolute inset-0 z-0">
        <video autoPlay loop muted playsInline className="h-full w-full object-cover" poster="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_strategy.png">
          <source src="/img/hotel_revenue_strategy.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0" style={{
        background: "linear-gradient(to top right, hsla(222, 47%, 7%, 0.9) 0%, hsla(222, 47%, 7%, 0.6) 40%, hsla(222, 47%, 7%, 0.3) 75%, hsla(222, 47%, 7%, 0.2) 100%)"
      }} />
      </div>

      <div className="pointer-events-none absolute inset-x-4 inset-y-4 z-10 border md:inset-x-8 md:inset-y-8" style={{
      borderColor: "hsla(43, 65%, 35%, 0.3)"
    }} aria-hidden="true" />

      <div className="absolute left-8 top-8 z-20 md:left-12 md:top-12">
        <span className="font-default text-xs tracking-widest uppercase" style={{
        color: "hsla(43, 65%, 35%, 0.9)"
      }}>
          {currentTime} <span className="mx-1 opacity-50">→</span> 21:00
        </span>
      </div>

      <div className="absolute right-8 top-8 z-20 md:right-12 md:top-12">
        <span className="font-default text-xs tracking-[0.3em] uppercase" style={{
        color: "hsla(43, 65%, 35%, 0.9)"
      }}>
          GROWTH
        </span>
      </div>

      <div className="relative z-20 flex min-h-screen flex-col justify-end px-8 pb-24 pt-32 md:justify-start md:px-16 md:pt-[60vh] md:pb-16 lg:px-24">
        <div className="max-w-4xl">
          <h1 className="font-default font-bold leading-[0.95] tracking-tight text-white" style={{
          fontSize: "clamp(3.5rem, 10vw, 10rem)"
        }}>
            Maximize Your{" "}
            <em className="italic font-normal" style={{
            color: "hsl(43, 65%, 35%)"
          }}>
              Hotel’s
            </em>
            ,
            <br />
            Revenue.
          </h1>

          <p className="mt-8 max-w-xl font-default text-lg md:text-xl" style={{
          color: "hsla(0, 0%, 100%, 0.85)"
        }}>
            Data-driven OTA strategies and expert revenue management for modern hospitality.
          </p>

          <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
            <Button asChild size="lg" className="h-14 bg-secondary px-8 font-default text-xs uppercase tracking-[0.2em] text-white hover:bg-secondary/90 rounded-lg w-full sm:w-auto">
              <Link to="/ContactPage">
                Book a Consultation
              </Link>
            </Button>

            <Button asChild variant="ghost" size="lg" className="h-14 px-8 font-default text-xs uppercase tracking-[0.2em] text-white border border-white/30 hover:bg-white/10 hover:text-white rounded-lg w-full sm:w-auto">
              <Link to="/OurServicesPage">
                <Play className="h-3 w-3" strokeWidth={1.5} />
                Explore Our Services
              </Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-12 right-8 z-20 hidden md:flex md:right-12 lg:right-16">
        <div className="flex flex-col items-center gap-4">
          <span className="font-default text-xs tracking-[0.3em] uppercase [writing-mode:vertical-rl] rotate-180 text-white">
            Scroll — REVENUE
          </span>
          <div className="relative h-20 w-px overflow-hidden" style={{
          backgroundColor: "hsla(43, 65%, 35%, 0.3)"
        }}>
            <div className="absolute top-0 left-0 w-full animate-scroll-line" style={{
            height: "40%",
            background: "linear-gradient(to bottom, transparent, hsl(43, 65%, 35%), transparent)"
          }} />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scroll-line {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(250%); }
        }
        .animate-scroll-line {
          animation: scroll-line 2s ease-in-out infinite;
        }
      `}</style>
    </section>;
}